import React from 'react';
import { Link } from 'react-router-dom';
import './Legal.css';

export const TermsOfService: React.FC = () => {
  return (
    <div className="legal-page">
      <header className="legal-header">
        <Link to="/" className="legal-back">
          <span className="back-arrow">&larr;</span> NS Gaming Studio
        </Link>
      </header>

      <main className="legal-content">
        <h1>Terms of Service</h1>
        <p className="legal-updated">Last Updated: March 4, 2026</p>

        <p>
          Welcome to NS Gaming Studio ("Service"), operated by NeuralSoft ("Company," "we," "us," or "our").
          By accessing or using the Service, you agree to be bound by these Terms of Service ("Terms").
          If you are under 18, a parent or legal guardian must review and agree to these Terms on your behalf
          before you use the Service.
        </p>

        <h2>1. Overview of the Service</h2>
        <p>
          NS Gaming Studio is an educational platform that allows users ages 8 and older to learn coding
          concepts by building HTML5 browser games through conversation with an AI assistant ("Game Buddy").
          The Service is intended for educational and recreational use.
        </p>

        <h2>2. Eligibility and Parental Consent</h2>
        <p>
          The Service is designed for users ages 8-16, though users of all ages are welcome.
          If you are under 13, a parent or legal guardian must create or approve the creation of your account,
          consent to these Terms, and consent to our <Link to="/privacy">Privacy Policy</Link> on your behalf,
          in compliance with the Children's Online Privacy Protection Act ("COPPA").
          If you are between 13 and 17, we strongly encourage parental involvement in your use of the Service.
        </p>

        <h2>3. Accounts</h2>
        <p>
          To use the Service, you must register for an account. You agree to provide accurate information and
          to keep your login credentials secure. You are responsible for all activity that occurs under your account.
          Parents or guardians are responsible for monitoring their child's use of the Service.
          We reserve the right to suspend or terminate accounts that violate these Terms.
        </p>

        <h2>4. Acceptable Use</h2>
        <p>You agree not to use the Service to:</p>
        <ul>
          <li>Create, share, or distribute content that is obscene, harmful, threatening, abusive, harassing, defamatory, or otherwise objectionable</li>
          <li>Attempt to circumvent the AI's content safety guidelines or topic boundaries</li>
          <li>Use automated tools, bots, or scripts to interact with the Service in an unauthorized manner</li>
          <li>Attempt to access other users' accounts, data, or projects without authorization</li>
          <li>Reverse-engineer, decompile, or attempt to extract the source code of the Service</li>
          <li>Use the Service for any commercial purpose without our written consent</li>
          <li>Violate any applicable local, state, national, or international law</li>
        </ul>

        <h2>5. User Content and Intellectual Property</h2>
        <p>
          <strong>Your games are yours.</strong> You retain ownership of the game code and creative content
          you produce using the Service. By publishing a game through our sharing feature, you grant
          NeuralSoft a non-exclusive, royalty-free license to host and display your published game through
          the Service for as long as you choose to keep it published. You may unpublish at any time.
        </p>
        <p>
          The Service itself, including its design, underlying software, AI system prompts, branding, and
          documentation, is the property of NeuralSoft and is protected by copyright, trademark, and other
          intellectual property laws. These Terms do not grant you any right to use the NeuralSoft or
          NS Gaming Studio name, logo, or trademarks.
        </p>

        <h2>6. AI-Generated Content</h2>
        <p>
          The AI assistant generates code and suggestions in response to user input. AI-generated content
          is provided "as is" for educational purposes. NeuralSoft does not guarantee that AI-generated code
          will be error-free, secure, or suitable for any particular purpose. We are not responsible for any
          consequences arising from the use, modification, or distribution of AI-generated code outside the Service.
        </p>

        <h2>7. Subscription Plans and Payments</h2>
        <p>
          The Service offers free and paid subscription tiers with different usage limits.
          Free accounts are subject to daily and monthly message limits.
          Paid plans provide higher usage limits and may include additional features.
          Plan pricing, features, and limits are subject to change with reasonable notice.
          All fees are non-refundable except as required by law or at our sole discretion.
        </p>

        <h2>8. Usage Limits and Fair Use</h2>
        <p>
          All accounts are subject to usage limits as described on our pricing page. We reserve the right
          to throttle or restrict access to accounts that we reasonably believe are abusing the Service,
          including but not limited to: automated usage, sharing credentials among multiple users, or
          circumventing usage limits.
        </p>

        <h2>9. Third-Party AI Providers</h2>
        <p>
          The Service uses third-party AI providers (including Anthropic, OpenAI, and Google) to generate
          responses. Your use of the Service is also subject to the terms and policies of these providers.
          We do not control these third-party services and are not liable for their availability, accuracy,
          or compliance. If you choose to provide your own API keys for these providers, you are responsible
          for compliance with their terms of service.
        </p>

        <h2>10. Privacy</h2>
        <p>
          Your privacy is important to us. Our collection and use of personal information is governed by
          our <Link to="/privacy">Privacy Policy</Link>, which is incorporated into these Terms by reference.
          Please review it carefully, especially if you are a parent or guardian of a child under 13.
        </p>

        <h2>11. Disclaimer of Warranties</h2>
        <p>
          THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS
          OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
          PARTICULAR PURPOSE, AND NON-INFRINGEMENT. WE DO NOT WARRANT THAT THE SERVICE WILL BE UNINTERRUPTED,
          SECURE, OR ERROR-FREE, OR THAT ANY DEFECTS WILL BE CORRECTED.
        </p>

        <h2>12. Limitation of Liability</h2>
        <p>
          TO THE MAXIMUM EXTENT PERMITTED BY LAW, NEURALSOFT AND ITS OFFICERS, DIRECTORS, EMPLOYEES, AND
          AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE
          DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF DATA, LOSS OF PROFITS, OR LOSS OF USE, ARISING OUT
          OF OR IN CONNECTION WITH YOUR USE OF THE SERVICE, WHETHER BASED ON WARRANTY, CONTRACT, TORT, OR
          ANY OTHER LEGAL THEORY, EVEN IF WE HAVE BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
          OUR TOTAL LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THESE TERMS OR THE SERVICE
          SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE TWELVE (12) MONTHS PRECEDING THE CLAIM, OR
          FIFTY DOLLARS ($50), WHICHEVER IS GREATER.
        </p>

        <h2>13. Indemnification</h2>
        <p>
          You agree to indemnify and hold harmless NeuralSoft and its officers, directors, employees, and
          agents from and against any and all claims, liabilities, damages, losses, and expenses (including
          reasonable attorneys' fees) arising out of or in connection with your use of the Service, your
          violation of these Terms, or your violation of any rights of a third party.
        </p>

        <h2>14. Termination</h2>
        <p>
          We may suspend or terminate your access to the Service at any time, with or without cause, and
          with or without notice. Upon termination, your right to use the Service will immediately cease.
          You may delete your account at any time. Sections 5, 6, 11, 12, 13, and 16 shall survive any
          termination of these Terms.
        </p>

        <h2>15. Modifications to Terms</h2>
        <p>
          We may update these Terms from time to time. If we make material changes, we will notify you by
          posting the updated Terms on the Service and updating the "Last Updated" date. Your continued use
          of the Service after the changes take effect constitutes your acceptance of the revised Terms.
          If you do not agree to the revised Terms, you must stop using the Service.
        </p>

        <h2>16. Governing Law and Dispute Resolution</h2>
        <p>
          These Terms shall be governed by and construed in accordance with the laws of the State of
          [YOUR STATE], without regard to its conflict of law provisions. Any disputes arising under or
          in connection with these Terms shall be resolved through binding arbitration in accordance with
          the rules of the American Arbitration Association, except that either party may seek injunctive
          relief in any court of competent jurisdiction. Class action lawsuits and class-wide arbitrations
          are not permitted.
        </p>

        <h2>17. Contact Us</h2>
        <p>
          If you have questions about these Terms, please contact us at:
        </p>
        {/*
          #125 fix (2026-05-17): **DEPLOYMENT BLOCKER** — these placeholders
          must be filled before production. Unfilled arbitration governing-
          law clause (`[YOUR STATE]` above) is unenforceable and exposes the
          company to class-action risk. Email + mailing address must be
          functional and consistent with PrivacyPolicy. CA SB 82 (effective
          Jan 1 2026) narrows arbitrable disputes for CA users — review with
          counsel. Have a lawyer sign off on final text before deploy.
        */}
        {(process.env.REACT_APP_ENVIRONMENT === 'production') && (
          <div role="alert" style={{ background: '#fee', border: '2px solid #c00', padding: '12px', margin: '12px 0', borderRadius: '6px' }}>
            <strong>⚠️ DEPLOYMENT BLOCKER:</strong> Contact info, state of
            governing law, and arbitration clause have unfilled placeholders.
            See #125 in the repair protocol.
          </div>
        )}
        <p>
          NeuralSoft<br />
          Email: [YOUR EMAIL ADDRESS]<br />
          Address: [YOUR MAILING ADDRESS]
        </p>

        <div className="legal-nav-links">
          <Link to="/privacy">Privacy Policy</Link>
          <Link to="/">Back to Home</Link>
        </div>
      </main>
    </div>
  );
};
